import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type Paginated, type ApiSuccess } from '../../lib/api';

interface Broker { id: string; name: string }

interface RequestRow {
  id: string;
  kind: 'SEARCH' | 'OFFER';
  name: string;
  phone: string;
  email: string | null;
  propertyType: string | null;
  purpose: string | null;
  city: string | null;
  district: string | null;
  minPrice: number | string | null;
  maxPrice: number | string | null;
  budgetLabel: string | null;
  areaMin: number | null;
  areaMax: number | null;
  rooms: number | null;
  notes: string | null;
  status: 'NEW' | 'IN_PROGRESS' | 'MATCHED' | 'CLOSED';
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  assignedBrokerId: string | null;
  assignedBroker?: Broker | null;
  createdAt: string;
  updatedAt: string;
}

interface RequestStats { new: number; inProgress: number; matched: number; closed: number }

const STATUS: Record<string, { label: string; color: 'amber' | 'navy' | 'green' | 'gray'; icon: string }> = {
  NEW:         { label: 'جديد',          color: 'amber', icon: '🆕' },
  IN_PROGRESS: { label: 'قيد المعالجة',  color: 'navy',  icon: '⚙️' },
  MATCHED:     { label: 'تم العثور',     color: 'green', icon: '✓' },
  CLOSED:      { label: 'مكتمل',         color: 'gray',  icon: '🔒' },
};

const PRIORITY: Record<string, { label: string; color: 'gray' | 'navy' | 'amber' | 'red' }> = {
  LOW:    { label: '⬇️ منخفض',  color: 'gray' },
  NORMAL: { label: 'عادي',      color: 'navy' },
  HIGH:   { label: '⬆️ مرتفع',  color: 'amber' },
  URGENT: { label: '🔥 عاجل',   color: 'red' },
};

export function AdminRequestsPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ q: '', status: '', kind: '', page: 1 });
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: stats } = useQuery({
    queryKey: ['admin', 'requests', 'stats'],
    queryFn: () => api.get<ApiSuccess<RequestStats>>('/api/requests/stats'),
    refetchInterval: 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'requests', filters],
    queryFn: () => api.get<Paginated<RequestRow>>('/api/requests', { query: { ...filters, limit: 30 } }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'requests'] });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RequestRow> }) => api.put(`/api/requests/${id}`, data),
    onSuccess: invalidate,
  });

  return (
    <div>
      {/* رأس الصفحة */}
      <div className="mb-6">
        <h1 className="text-3xl font-black text-navy-dark">📋 إدارة الطلبات</h1>
        <p className="text-sm text-ink-muted mt-1">طلبات بحث/عرض عقارات من الزوار والمستخدمين — يصل إيميل تأكيد تلقائي للمرسل</p>
      </div>

      {/* الإحصاءات */}
      {stats?.data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="جديد"          value={stats.data.new}        color="amber" icon="🆕"
            active={filters.status === 'NEW'} onClick={() => setFilters({ ...filters, status: filters.status === 'NEW' ? '' : 'NEW', page: 1 })} pulse />
          <StatCard label="قيد المعالجة"  value={stats.data.inProgress} color="navy"  icon="⚙️"
            active={filters.status === 'IN_PROGRESS'} onClick={() => setFilters({ ...filters, status: filters.status === 'IN_PROGRESS' ? '' : 'IN_PROGRESS', page: 1 })} />
          <StatCard label="تم العثور"     value={stats.data.matched}    color="green" icon="✓"
            active={filters.status === 'MATCHED'} onClick={() => setFilters({ ...filters, status: filters.status === 'MATCHED' ? '' : 'MATCHED', page: 1 })} />
          <StatCard label="مكتمل"          value={stats.data.closed}     color="gray"  icon="🔒"
            active={filters.status === 'CLOSED'} onClick={() => setFilters({ ...filters, status: filters.status === 'CLOSED' ? '' : 'CLOSED', page: 1 })} />
        </div>
      )}

      {/* الفلاتر */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <Input
                placeholder="🔍 بحث (الاسم/الإيميل/الجوال)…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })}
              />
            </div>
            <select
              value={filters.kind}
              onChange={(e) => setFilters({ ...filters, kind: e.target.value, page: 1 })}
              className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              <option value="">كل الأنواع</option>
              <option value="SEARCH">🔍 بحث عن عقار</option>
              <option value="OFFER">🏠 عرض عقار</option>
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              <option value="">كل الحالات</option>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
            </select>
          </div>
        </CardBody>
      </Card>

      {/* الجدول */}
      <Card>
        <CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>النوع</Th>
                <Th>المرسل</Th>
                <Th>المدينة / النوع</Th>
                <Th>الميزانية</Th>
                <Th>الحالة</Th>
                <Th>الأولوية</Th>
                <Th>المسوّق</Th>
                <Th>التاريخ</Th>
                <Th>إجراءات</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9} className="text-center py-12 text-gray-500">⟳ جاري التحميل…</td></tr>
              ) : !data?.data?.length ? (
                <tr><td colSpan={9} className="text-center py-12 text-gray-500">
                  <div className="text-5xl mb-2">📭</div>
                  لا توجد طلبات تطابق الفلاتر
                </td></tr>
              ) : data.data.map((r) => (
                <tr key={r.id} className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition ${r.status === 'NEW' ? 'bg-amber-50/30' : ''}`} onClick={() => setOpenId(r.id)}>
                  <Td>
                    <Badge color={r.kind === 'OFFER' ? 'gold' : 'navy'}>
                      {r.kind === 'OFFER' ? '🏠 عرض' : '🔍 بحث'}
                    </Badge>
                  </Td>
                  <Td>
                    <div className="font-bold text-navy-dark">{r.name}</div>
                    <div className="text-[10px] text-ink-muted" dir="ltr">{r.phone}</div>
                  </Td>
                  <Td>
                    <div className="text-xs">{r.city ?? '—'}</div>
                    <div className="text-[10px] text-ink-muted">{r.propertyType ?? '—'}</div>
                  </Td>
                  <Td className="text-xs">{formatBudget(r)}</Td>
                  <Td><Badge color={STATUS[r.status]?.color}>{STATUS[r.status]?.icon} {STATUS[r.status]?.label}</Badge></Td>
                  <Td><Badge color={PRIORITY[r.priority]?.color}>{PRIORITY[r.priority]?.label}</Badge></Td>
                  <Td className="text-xs">{r.assignedBroker?.name ?? <span className="text-ink-muted">—</span>}</Td>
                  <Td className="text-xs whitespace-nowrap">{new Date(r.createdAt).toLocaleDateString('ar-SA')}</Td>
                  <Td>
                    <Button size="sm" variant="primary">📂 فتح</Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {/* الترقيم */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button size="sm" variant="outline" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>السابق</Button>
          <span className="px-4 py-2 text-sm font-bold">{filters.page} / {data.pagination.totalPages}</span>
          <Button size="sm" variant="outline" disabled={filters.page >= data.pagination.totalPages} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>التالي</Button>
        </div>
      )}

      {/* المودال */}
      {openId && (
        <RequestDetailModal
          requestId={openId}
          onClose={() => setOpenId(null)}
          onUpdated={invalidate}
        />
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ مودال تفاصيل الطلب + التحكم الكامل ━━━━━━━━━━━━━━━━━━━━
function RequestDetailModal({ requestId, onClose, onUpdated }: {
  requestId: string;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'request', requestId],
    queryFn: () => api.get<ApiSuccess<RequestRow>>(`/api/requests/${requestId}`),
  });

  // قائمة المسوّقين للإسناد
  const { data: brokers } = useQuery({
    queryKey: ['admin', 'brokers-simple'],
    queryFn: () => api.get<Paginated<Broker & { role: string }>>('/api/admin/users', {
      query: { role: 'BROKER', limit: 50 },
    }).catch(() => ({ data: [] }) as Paginated<Broker & { role: string }>),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'request', requestId] });
    onUpdated();
  };

  const updateMut = useMutation({
    mutationFn: (changes: Partial<RequestRow>) => api.put(`/api/requests/${requestId}`, changes),
    onSuccess: refresh,
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/api/requests/${requestId}`),
    onSuccess: () => { onUpdated(); onClose(); },
  });

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notes, setNotes] = useState<string>('');
  const [notesInit, setNotesInit] = useState(false);

  if (isLoading || !data?.data) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 text-gray-500">⟳ جاري التحميل…</div>
      </div>
    );
  }

  const r = data.data;
  if (!notesInit) { setNotes(r.notes ?? ''); setNotesInit(true); }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        {/* رأس */}
        <div className="bg-gradient-to-l from-navy-darker to-navy-dark text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-2xl">
              {r.kind === 'OFFER' ? '🏠' : '🔍'}
            </div>
            <div className="min-w-0">
              <h2 className="font-black text-lg truncate">
                {r.kind === 'OFFER' ? 'عرض عقار' : 'بحث عن عقار'} — {r.name}
              </h2>
              <p className="text-xs text-white/70">
                #{r.id.slice(-12).toUpperCase()} · {new Date(r.createdAt).toLocaleString('ar-SA')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl shrink-0">✕</button>
        </div>

        <div className="p-6 space-y-5">
          {/* تحكم الحالة (أزرار سريعة) */}
          <div>
            <div className="text-xs font-bold text-ink-muted mb-2">🎛️ تغيير الحالة:</div>
            <div className="flex flex-wrap gap-2">
              {(['NEW', 'IN_PROGRESS', 'MATCHED', 'CLOSED'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => updateMut.mutate({ status: s })}
                  disabled={r.status === s || updateMut.isPending}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition ${
                    r.status === s
                      ? 'bg-navy-dark text-white border-navy-dark'
                      : 'bg-white border-gray-200 hover:border-navy text-ink-secondary hover:text-navy-dark'
                  }`}
                >
                  {STATUS[s].icon} {STATUS[s].label}
                </button>
              ))}
            </div>
          </div>

          {/* تحكم الأولوية */}
          <div>
            <div className="text-xs font-bold text-ink-muted mb-2">⚡ الأولوية:</div>
            <div className="flex flex-wrap gap-2">
              {(['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => updateMut.mutate({ priority: p })}
                  disabled={r.priority === p || updateMut.isPending}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition ${
                    r.priority === p
                      ? 'bg-navy-dark text-white border-navy-dark'
                      : 'bg-white border-gray-200 hover:border-navy text-ink-secondary'
                  }`}
                >
                  {PRIORITY[p].label}
                </button>
              ))}
            </div>
          </div>

          {/* بيانات المرسل */}
          <div>
            <h3 className="text-sm font-black text-navy-dark mb-2 border-r-4 border-gold pr-2">👤 بيانات المرسل</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="الاسم" value={r.name} />
              <Field label="الجوال" value={r.phone} dir="ltr" copyable />
              <Field label="الإيميل" value={r.email ?? '—'} dir="ltr" copyable={!!r.email} />
            </div>
          </div>

          {/* تفاصيل العقار */}
          <div>
            <h3 className="text-sm font-black text-navy-dark mb-2 border-r-4 border-gold pr-2">🏠 تفاصيل العقار المطلوب</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="نوع العقار" value={r.propertyType ?? '—'} />
              <Field label="الغرض" value={r.purpose ?? '—'} />
              <Field label="المدينة" value={r.city ?? '—'} />
              <Field label="الحي" value={r.district ?? '—'} />
              <Field label="عدد الغرف" value={r.rooms?.toString() ?? '—'} />
              <Field label="المساحة" value={r.areaMin || r.areaMax ? `${r.areaMin ?? '?'} - ${r.areaMax ?? '?'} م²` : '—'} />
              <div className="md:col-span-3">
                <Field label="الميزانية" value={formatBudget(r)} />
              </div>
            </div>
          </div>

          {/* إسناد لمسوّق */}
          <div>
            <h3 className="text-sm font-black text-navy-dark mb-2 border-r-4 border-gold pr-2">🤝 إسناد لمسوّق</h3>
            <select
              value={r.assignedBrokerId ?? ''}
              onChange={(e) => updateMut.mutate({ assignedBrokerId: e.target.value || null })}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              <option value="">— بدون إسناد —</option>
              {brokers?.data?.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            {r.assignedBroker && (
              <div className="text-[11px] text-ink-muted mt-1">
                مُسنَد إلى: <strong>{r.assignedBroker.name}</strong>
              </div>
            )}
          </div>

          {/* ملاحظات داخلية */}
          <div>
            <h3 className="text-sm font-black text-navy-dark mb-2 border-r-4 border-gold pr-2">📝 ملاحظات داخلية (لا يراها المستخدم)</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="ملاحظات لمتابعة الطلب…"
              className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm resize-y"
            />
            <div className="flex justify-end mt-2">
              <Button
                size="sm"
                variant="primary"
                onClick={() => updateMut.mutate({ notes: notes.trim() || null })}
                loading={updateMut.isPending}
                disabled={notes === (r.notes ?? '')}
              >
                💾 حفظ الملاحظات
              </Button>
            </div>
          </div>

          {/* رسالة المرسل (notes الأصلية إن كانت من المستخدم) */}
          {r.notes && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <div className="text-[11px] font-bold text-blue-900 mb-1">💬 ملاحظات المرسل الأصلية:</div>
              <div className="text-xs text-blue-900 whitespace-pre-wrap leading-relaxed">{r.notes}</div>
            </div>
          )}
        </div>

        {/* أزرار الأكشن السفلية */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between flex-wrap gap-2 bg-gray-50 rounded-b-2xl">
          {confirmDelete ? (
            <div className="flex gap-2 items-center">
              <span className="text-xs text-red-700 font-bold">حذف الطلب نهائياً؟</span>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>إلغاء</Button>
              <Button
                size="sm"
                variant="primary"
                className="!bg-red-600 hover:!bg-red-700"
                onClick={() => deleteMut.mutate()}
                loading={deleteMut.isPending}
              >
                🗑️ نعم احذف
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)} className="!text-red-600">
              🗑️ حذف الطلب
            </Button>
          )}
          <div className="flex gap-2">
            {r.email && (
              <a
                href={`mailto:${r.email}?subject=بخصوص طلبك في شركة صورة العقارية`}
                className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-bold transition"
              >
                ✉️ راسل
              </a>
            )}
            <a
              href={`tel:${r.phone}`}
              className="px-3 py-2 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 text-xs font-bold transition"
            >
              📞 اتصل
            </a>
            <Button variant="ghost" onClick={onClose}>إغلاق</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Helpers ━━━━━━━━━━━━━━━━━━━━
function formatBudget(r: { budgetLabel: string | null; minPrice: number | string | null; maxPrice: number | string | null }): string {
  if (r.budgetLabel) return r.budgetLabel;
  const min = r.minPrice ? Number(r.minPrice) : null;
  const max = r.maxPrice ? Number(r.maxPrice) : null;
  if (min && max) return `${min.toLocaleString('en-US')} - ${max.toLocaleString('en-US')}`;
  if (max) return `حتى ${max.toLocaleString('en-US')}`;
  if (min) return `من ${min.toLocaleString('en-US')}`;
  return '—';
}

function StatCard({ label, value, color, icon, onClick, active, pulse }: {
  label: string; value: number; color: 'amber' | 'navy' | 'green' | 'gray';
  icon: string; onClick: () => void; active?: boolean; pulse?: boolean;
}) {
  const colors = {
    amber: { active: 'border-amber-400 bg-amber-50', text: 'text-amber-700' },
    navy:  { active: 'border-navy bg-navy-soft',     text: 'text-navy-dark' },
    green: { active: 'border-green-400 bg-green-50', text: 'text-green-700' },
    gray:  { active: 'border-gray-400 bg-gray-100',  text: 'text-gray-700' },
  }[color];
  return (
    <button
      onClick={onClick}
      className={`text-right p-3 rounded-2xl border-2 bg-white transition hover:shadow-brand ${
        active ? colors.active : 'border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-2xl">{icon}</span>
        {pulse && value > 0 && <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />}
      </div>
      <div className={`text-2xl font-black mt-1 ${colors.text}`}>{value}</div>
      <div className="text-xs text-ink-muted">{label}</div>
    </button>
  );
}

function Field({ label, value, dir, copyable }: { label: string; value: string; dir?: string; copyable?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!copyable) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
      <div className="text-[10px] text-ink-muted mb-1">{label}</div>
      <div className="flex items-center justify-between gap-2">
        <span className="font-bold text-sm truncate" dir={dir}>{value}</span>
        {copyable && (
          <button
            onClick={copy}
            className="text-[10px] px-2 py-0.5 rounded bg-white border border-gray-200 hover:bg-navy-soft hover:border-navy text-ink-muted hover:text-navy-dark shrink-0"
            title="نسخ"
          >
            {copied ? '✓' : '📋'}
          </button>
        )}
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase whitespace-nowrap">{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}
